<script setup>
// Addendum §5 DateRangePicker.
//   • Presets incl. This FY to date / Last FY — FY START IS PER-FIRM CONFIG
//     (fyStartMonth/fyStartDay), never a hard-coded July or April, so the same
//     code serves AU, UK and US firms.
//   • Dates are keyboard-enterable: the two fields are native <input type="date">
//     — accessible and text-enterable by definition, no drag-only calendar.
//   • A range crossing a locked period is PERMITTED but FLAGGED (lockedBefore).
//   • Emits { start, end, preset } — downstream reports need to know "Last FY"
//     was chosen, not just the resolved dates, so a saved report re-resolves.
import { ref, computed, onBeforeUnmount, nextTick } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
  fyStartMonth: { type: Number, default: 7 }, // 1-indexed; AU default = July
  fyStartDay: { type: Number, default: 1 },
  lockedBefore: { type: String, default: null }, // 'YYYY-MM-DD'; ranges starting before are flagged
})
const model = defineModel({ default: () => ({ start: null, end: null, preset: 'custom' }) })

const open = ref(false)
const root = ref(null)
const draft = ref({ start: model.value.start, end: model.value.end, preset: model.value.preset })

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const today = () => new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate())

function fyStartFor(year) {
  return new Date(year, props.fyStartMonth - 1, props.fyStartDay)
}
function currentFyStart() {
  const t = today()
  let s = fyStartFor(t.getFullYear())
  if (t < s) s = fyStartFor(t.getFullYear() - 1)
  return s
}

const PRESETS = [
  { key: 'this-week', label: 'This week' },
  { key: 'last-week', label: 'Last week' },
  { key: 'this-month', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'this-fy', label: 'This FY to date' },
  { key: 'last-fy', label: 'Last FY' },
  { key: 'this-cal-year', label: 'This calendar year' },
  { key: 'custom', label: 'Custom' },
]

function resolvePreset(key) {
  const t = today()
  const startOfWeek = (d) => { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); return x }
  switch (key) {
    case 'this-week': { const s = startOfWeek(t); const e = new Date(s); e.setDate(s.getDate() + 6); return [s, e] }
    case 'last-week': { const s = startOfWeek(t); s.setDate(s.getDate() - 7); const e = new Date(s); e.setDate(s.getDate() + 6); return [s, e] }
    case 'this-month': return [new Date(t.getFullYear(), t.getMonth(), 1), new Date(t.getFullYear(), t.getMonth() + 1, 0)]
    case 'last-month': return [new Date(t.getFullYear(), t.getMonth() - 1, 1), new Date(t.getFullYear(), t.getMonth(), 0)]
    case 'this-fy': { const s = currentFyStart(); return [s, t] }
    case 'last-fy': { const s = new Date(currentFyStart()); s.setFullYear(s.getFullYear() - 1); const e = new Date(currentFyStart()); e.setDate(e.getDate() - 1); return [s, e] }
    case 'this-cal-year': return [new Date(t.getFullYear(), 0, 1), new Date(t.getFullYear(), 11, 31)]
    default: return null
  }
}

function choosePreset(key) {
  if (key === 'custom') { draft.value.preset = 'custom'; return }
  const [s, e] = resolvePreset(key)
  draft.value = { start: iso(s), end: iso(e), preset: key }
}

const crossesLocked = computed(() => {
  if (!props.lockedBefore || !draft.value.start) return false
  return draft.value.start < props.lockedBefore
})

const displayLabel = computed(() => {
  const p = PRESETS.find((x) => x.key === model.value.preset)
  if (p && p.key !== 'custom') return p.label
  if (model.value.start && model.value.end) return `${model.value.start} → ${model.value.end}`
  return 'Select date range'
})

function apply() {
  model.value = { ...draft.value }
  open.value = false
}
function onDocPointer(e) { if (root.value && !root.value.contains(e.target)) open.value = false }
async function toggle() {
  open.value = !open.value
  if (open.value) {
    draft.value = { ...model.value }
    document.addEventListener('pointerdown', onDocPointer, true)
    await nextTick()
  } else {
    document.removeEventListener('pointerdown', onDocPointer, true)
  }
}
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer, true))
</script>

<template>
  <div ref="root" class="l-daterange">
    <button type="button" class="trigger" :aria-expanded="open" @click="toggle">
      <LIcon name="calendar-days" :size="15" class="lead" />
      <span class="val">{{ displayLabel }}</span>
      <LIcon name="chevron-down" :size="14" class="chev" />
    </button>

    <div v-if="open" class="pop">
      <ul class="presets">
        <li v-for="p in PRESETS" :key="p.key">
          <button
            type="button"
            class="preset"
            :class="{ on: draft.preset === p.key }"
            @click="choosePreset(p.key)"
          >{{ p.label }}</button>
        </li>
      </ul>
      <div class="custom">
        <label class="fld">
          <span>Start</span>
          <input type="date" v-model="draft.start" @input="draft.preset = 'custom'" />
        </label>
        <label class="fld">
          <span>End</span>
          <input type="date" v-model="draft.end" :min="draft.start || undefined" @input="draft.preset = 'custom'" />
        </label>
        <div v-if="crossesLocked" class="flag">
          <LIcon name="triangle-exclamation" :size="12" />
          This range starts before a locked period. Locked time is included but flagged.
        </div>
        <div class="apply">
          <button type="button" class="apply-btn" :disabled="!draft.start || !draft.end" @click="apply">Apply</button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.l-daterange {
  position: relative;
  display: inline-block;
}
.trigger {
  display: inline-flex;
  align-items: center;
  gap: var(--sp-2);
  font-family: var(--font-ui);
  font-size: var(--text-sm);
  color: var(--text);
  padding: 9px 12px;
  border: 1px solid var(--line);
  border-radius: var(--r);
  background: var(--surface);
  min-height: var(--target-min);
  cursor: pointer;
}
.trigger .lead { color: var(--icon); }
.trigger .chev { color: var(--icon); }
.val { font-weight: 500; }
.pop {
  position: absolute;
  z-index: 70;
  top: calc(100% + 6px);
  left: 0;
  display: flex;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--r);
  box-shadow: var(--shadow);
  overflow: hidden;
}
.presets {
  list-style: none;
  padding: 6px;
  border-right: 1px solid var(--line-soft);
  min-width: 168px;
}
.preset {
  display: block;
  width: 100%;
  text-align: left;
  font-size: var(--text-sm);
  color: var(--text-2);
  padding: 7px 10px;
  border-radius: var(--r-sm);
  min-height: var(--target-min);
  background: none;
  border: 0;
  cursor: pointer;
}
.preset:hover { background: var(--surface-sunken); color: var(--text); }
.preset.on { background: var(--interactive-tint); color: var(--interactive-text); font-weight: 600; }
.custom {
  padding: var(--sp-4);
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  min-width: 240px;
}
.fld {
  display: flex;
  flex-direction: column;
  gap: 5px;
  font-size: var(--text-chip);
  font-weight: 600;
  color: var(--text-2);
}
.fld input {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-size: var(--text-base);
  color: var(--text);
  padding: 8px 10px;
  border: 1px solid var(--line);
  border-radius: var(--r);
  background: var(--surface);
  min-height: var(--target-min);
}
.fld input:focus-visible { border-color: var(--interactive); }
.flag {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: var(--text-chip);
  color: var(--status-pending-text);
  background: var(--status-pending-fill);
  padding: 7px 9px;
  border-radius: var(--r-sm);
}
.apply { display: flex; justify-content: flex-end; }
.apply-btn {
  font-family: var(--font-ui);
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--interactive-on);
  background: var(--interactive);
  border: 0;
  border-radius: var(--r);
  padding: 8px 16px;
  min-height: var(--target-min);
  cursor: pointer;
}
.apply-btn:hover:not(:disabled) { background: var(--interactive-hover); }
.apply-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
